/**
 * NovelSectionRenderers - React components for novel Page Builder templates
 * Location: /app/frontend/src/components/page-builder/novel/
 * 
 * These components render the novel detail templates within the Page Builder framework.
 * They fetch their own data based on the novelId from URL params or context.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../../App';
import { apiClient } from '../../../config/api';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { Textarea } from '../../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  Heart,
  Share2,
  DollarSign,
  Eye,
  Users,
  Star,
  BookOpen,
  Lock,
  Clock,
  ChevronRight,
  MessageCircle,
  Flame,
  Laugh,
  Zap,
  AlertTriangle,
  Loader2,
  UserPlus,
  BookText,
  FileText,
  Calendar,
  Globe,
  Shield,
} from 'lucide-react';

// ============================================================================
// SHARED HOOKS & UTILITIES
// ============================================================================

/**
 * Hook to fetch novel data - shared across section renderers
 */
export const useNovelData = (novelId) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!novelId) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const response = await apiClient.get(`/novels/${novelId}/detail`);
        setData(response.data);
        setError(null);
      } catch (err) {
        console.error('[useNovelData] Error:', err);
        if (err.response?.status === 404) {
          setError('Novel not found');
        } else {
          // Fallback to basic novel endpoint if detail doesn't exist
          try {
            const fallbackResponse = await apiClient.get(`/novels/${novelId}`);
            setData({ novel: fallbackResponse.data, creator: null, stats: {} });
            setError(null);
          } catch (fallbackErr) {
            if (fallbackErr.response?.status === 404) {
              setError('Novel not found');
            } else {
              setError(fallbackErr.message);
            }
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [novelId]);

  return { data, loading, error, refetch: () => setLoading(true) };
};

/**
 * Hook to fetch chapters
 */
export const useChapters = (novelId) => {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!novelId) {
      setLoading(false);
      return;
    }

    const fetchChapters = async () => {
      try {
        const response = await apiClient.get(`/novels/${novelId}/chapters`);
        setChapters(response.data || []);
        setError(null);
      } catch (err) {
        console.error('[useChapters] Error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchChapters();
  }, [novelId]);

  return { chapters, setChapters, loading, error };
};

/**
 * Hook to fetch comments (reuses series comments pattern)
 */
export const useNovelComments = (novelId, limit = 10) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!novelId) {
      setLoading(false);
      return;
    }

    const fetchComments = async () => {
      try {
        // Try novel-specific comments endpoint, fallback to empty
        const response = await apiClient.get(`/novels/${novelId}/comments?limit=${limit}`);
        setComments(response.data || []);
        setError(null);
      } catch (err) {
        console.error('[useNovelComments] Error:', err);
        setComments([]);
        setError(null); // Don't show error for missing comments
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, [novelId, limit]);

  return { comments, setComments, loading, error };
};

/**
 * Hook to fetch related novels
 */
export const useRelatedNovels = (novelId, limit = 6) => {
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!novelId) {
      setLoading(false);
      return;
    }

    const fetchRelated = async () => {
      try {
        const response = await apiClient.get(`/novels/${novelId}/related?limit=${limit}`);
        setRelated(response.data || []);
        setError(null);
      } catch (err) {
        console.error('[useRelatedNovels] Error:', err);
        // Fallback to general novels list
        try {
          const fallbackResponse = await apiClient.get(`/novels?limit=${limit}`);
          const novels = fallbackResponse.data?.items || fallbackResponse.data || [];
          setRelated(novels.filter(n => n.id !== novelId).slice(0, limit));
          setError(null);
        } catch {
          setRelated([]);
          setError(null);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRelated();
  }, [novelId, limit]);

  return { related, loading, error };
};

/**
 * Section loading skeleton
 */
const SectionLoading = ({ className = '' }) => (
  <div className={`animate-pulse ${className}`}>
    <div className="h-8 bg-zinc-800 rounded w-1/3 mb-4" />
    <div className="space-y-3">
      <div className="h-4 bg-zinc-800 rounded w-full" />
      <div className="h-4 bg-zinc-800 rounded w-5/6" />
      <div className="h-4 bg-zinc-800 rounded w-4/6" />
    </div>
  </div>
);

/**
 * Section error state
 */
const SectionError = ({ message, onRetry }) => (
  <div className="text-center py-8 bg-red-500/10 border border-red-500/20 rounded-lg">
    <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
    <p className="text-red-400 text-sm mb-3">{message || 'Failed to load content'}</p>
    {onRetry && (
      <Button onClick={onRetry} variant="outline" size="sm" className="text-red-400 border-red-500/50">
        Retry
      </Button>
    )}
  </div>
);

// ============================================================================
// NOVEL HERO SECTION
// ============================================================================

/**
 * NovelHeroSection - Dynamic hero with cover, title, creator info
 */
export const NovelHeroSection = ({ 
  block, 
  novelId: propNovelId,
  isPreview = false 
}) => {
  const { novelId: paramNovelId } = useParams();
  const novelId = propNovelId || paramNovelId;
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const { data, loading, error } = useNovelData(novelId);
  const [favorited, setFavorited] = useState(false);
  const [showTipModal, setShowTipModal] = useState(false);

  const props = block?.props || {};

  const handleFavorite = async () => {
    if (!user) {
      toast.error('Please login to favorite');
      navigate('/auth');
      return;
    }

    try {
      if (favorited) {
        // Would call unfavorite endpoint
        setFavorited(false);
        toast.success('Removed from favorites');
      } else {
        // Would call favorite endpoint
        setFavorited(true);
        toast.success('Added to favorites! ❤️');
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
      toast.error('Failed to update favorites');
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: data?.novel?.title,
        text: data?.novel?.description,
        url,
      });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <section className="relative min-h-[400px] bg-zinc-900" data-testid="novel-hero-loading">
        <div className="container mx-auto px-6 py-12">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="w-64 h-96 bg-zinc-800 rounded-lg animate-pulse" />
            <div className="flex-1 space-y-4">
              <div className="h-12 bg-zinc-800 rounded w-2/3 animate-pulse" />
              <div className="h-6 bg-zinc-800 rounded w-1/3 animate-pulse" />
              <div className="h-8 bg-zinc-800 rounded w-1/2 animate-pulse" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className="relative min-h-[300px] bg-zinc-900 flex items-center justify-center">
        <SectionError message="Failed to load novel data" />
      </section>
    );
  }

  const { novel, creator, stats } = data;

  return (
    <section 
      className="relative" 
      data-testid="novel-hero-section"
      data-novel-id={novelId}
    >
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: novel?.cover_url
            ? `url('${novel.cover_url}')`
            : 'linear-gradient(to bottom right, #18181b, #000000)',
          filter: 'blur(20px)',
          transform: 'scale(1.1)',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-black" />

      <div className="relative container mx-auto px-6 py-12">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Cover Image */}
          {props.showCover !== false && (
            <div className="flex-shrink-0">
              <div className="w-64 h-96 bg-black/60 rounded-lg shadow-2xl border-2 border-orange-500 flex items-center justify-center overflow-hidden">
                {novel?.cover_url ? (
                  <img
                    src={novel.cover_url}
                    alt={novel.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <BookText className="w-16 h-16 text-zinc-600" />
                )}
              </div>
            </div>
          )}

          {/* Info */}
          <div className="flex-1">
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
              {novel?.title || 'Novel Title'}
            </h1>

            {/* Genre Tags */}
            {props.showTags !== false && novel?.genres && (
              <div className="flex flex-wrap gap-2 mb-4">
                {novel.genres.map((genre, i) => (
                  <Badge key={i} variant="secondary" className="bg-orange-500/20 text-orange-400 border border-orange-500/30">
                    {genre}
                  </Badge>
                ))}
              </div>
            )}

            {/* Creator Info */}
            <div className="flex items-center gap-3 mb-6">
              <Avatar className="h-10 w-10 border-2 border-orange-500">
                <AvatarImage src={creator?.avatar_url} />
                <AvatarFallback className="bg-orange-500 text-white">
                  {(creator?.display_name || novel?.author_name || 'A')[0].toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <Link 
                  to={`/u/${creator?.handle || novel?.creator_id}`}
                  className="text-white font-medium hover:text-orange-400 transition-colors"
                >
                  {creator?.display_name || novel?.author_name || 'Author'}
                </Link>
                <p className="text-sm text-zinc-400">@{creator?.handle || 'author'}</p>
              </div>
            </div>

            {/* Stats */}
            {props.showStats !== false && (
              <div className="flex flex-wrap gap-6 mb-6">
                <div className="flex items-center gap-2 text-zinc-300">
                  <Eye className="w-5 h-5 text-orange-400" />
                  <span>{(stats?.views || novel?.metrics_cache?.views || 0).toLocaleString()} views</span>
                </div>
                <div className="flex items-center gap-2 text-zinc-300">
                  <Users className="w-5 h-5 text-orange-400" />
                  <span>{(stats?.subscribers || 0).toLocaleString()} readers</span>
                </div>
                <div className="flex items-center gap-2 text-zinc-300">
                  <FileText className="w-5 h-5 text-orange-400" />
                  <span>{(stats?.chapters || novel?.metrics_cache?.chapters || 0)} chapters</span>
                </div>
                <div className="flex items-center gap-2 text-zinc-300">
                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  <span>{(stats?.rating || 8.5).toFixed(1)}/10</span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={handleFavorite}
                variant={favorited ? 'default' : 'outline'}
                className={favorited 
                  ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                  : 'border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white'
                }
              >
                <Heart className={`w-4 h-4 mr-2 ${favorited ? 'fill-current' : ''}`} />
                {favorited ? 'Favorited' : 'Favorite'}
              </Button>

              <Dialog open={showTipModal} onOpenChange={setShowTipModal}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-green-500 text-green-400 hover:bg-green-500 hover:text-white">
                    <DollarSign className="w-4 h-4 mr-2" />
                    Tip Author
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-zinc-700">
                  <DialogHeader>
                    <DialogTitle className="text-white">Support {creator?.display_name || 'the Author'}</DialogTitle>
                    <DialogDescription className="text-zinc-400">
                      Show appreciation for their work
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-3 gap-3 py-4">
                    {[2, 5, 10].map(amount => (
                      <Button 
                        key={amount}
                        className="bg-green-500 hover:bg-green-600"
                        onClick={() => {
                          toast.success(`Tip feature coming soon!`);
                          setShowTipModal(false);
                        }}
                      >
                        ${amount}
                      </Button>
                    ))}
                  </div>
                </DialogContent>
              </Dialog>

              {props.showShareButton !== false && (
                <Button variant="outline" onClick={handleShare} className="border-zinc-600">
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              )}
            </div>

            {/* Read CTA */}
            <div className="mt-6">
              <Link to={`/novel/${novelId}/read/first`}>
                <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-8">
                  <BookOpen className="w-5 h-5 mr-2" />
                  Start Reading
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// CHAPTER LIST SECTION
// ============================================================================

export const ChapterListSection = ({
  block,
  novelId: propNovelId,
  isPreview = false,
}) => {
  const { novelId: paramNovelId } = useParams();
  const novelId = propNovelId || paramNovelId;
  const { chapters, loading, error } = useChapters(novelId);
  const [sortOrder, setSortOrder] = useState('newest');

  const props = block?.props || {};

  if (loading) {
    return (
      <section className="py-8 bg-zinc-900/50">
        <div className="container mx-auto px-6">
          <div className="h-8 bg-zinc-800 rounded w-1/4 mb-6 animate-pulse" />
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-16 bg-zinc-800 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-8 bg-zinc-900/50">
        <div className="container mx-auto px-6">
          <SectionError message="Failed to load chapters" />
        </div>
      </section>
    );
  }

  const sortedChapters = [...chapters].sort((a, b) => {
    const orderA = a.chapter_number || a.order || 0;
    const orderB = b.chapter_number || b.order || 0;
    return sortOrder === 'newest' ? orderB - orderA : orderA - orderB;
  });

  return (
    <section className="py-8 bg-zinc-900/50" data-testid="chapter-list-section">
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-orange-500" />
            {props.heading || 'Chapters'}
            <Badge variant="secondary" className="ml-2 bg-zinc-800 text-zinc-300">
              {chapters.length}
            </Badge>
          </h2>
          
          {props.allowSort !== false && (
            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger className="w-[140px] bg-zinc-800 border-zinc-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="oldest">Oldest First</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {sortedChapters.length === 0 ? (
          <div className="text-center py-12 bg-zinc-800/30 rounded-lg">
            <BookOpen className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No chapters published yet</p>
            <p className="text-zinc-500 text-sm mt-1">Check back soon!</p>
          </div>
        ) : (
          <div className="space-y-2">
            {sortedChapters.map((chapter, index) => (
              <Link
                key={chapter.id || index}
                to={`/novel/${novelId}/read/${chapter.id}`}
                className="flex items-center justify-between p-4 bg-zinc-800/50 hover:bg-zinc-800 rounded-lg transition-colors group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center text-orange-400 font-bold">
                    {chapter.chapter_number || index + 1}
                  </div>
                  <div>
                    <h3 className="text-white font-medium group-hover:text-orange-400 transition-colors">
                      {chapter.title || `Chapter ${chapter.chapter_number || index + 1}`}
                    </h3>
                    <p className="text-sm text-zinc-500">
                      {chapter.word_count?.toLocaleString() || '~'} words
                      {chapter.published_at && (
                        <span className="ml-2">
                          • {new Date(chapter.published_at).toLocaleDateString()}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {chapter.is_premium && (
                    <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                      <Lock className="w-3 h-3 mr-1" />
                      Premium
                    </Badge>
                  )}
                  <ChevronRight className="w-5 h-5 text-zinc-500 group-hover:text-orange-400 transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// ============================================================================
// NOVEL INFO PANEL SECTION
// ============================================================================

export const NovelInfoPanelSection = ({
  block,
  novelId: propNovelId,
  isPreview = false,
}) => {
  const { novelId: paramNovelId } = useParams();
  const novelId = propNovelId || paramNovelId;
  const { data, loading, error } = useNovelData(novelId);

  const props = block?.props || {};

  if (loading) {
    return (
      <section className="py-8">
        <div className="container mx-auto px-6">
          <SectionLoading />
        </div>
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className="py-8">
        <div className="container mx-auto px-6">
          <SectionError message="Failed to load novel info" />
        </div>
      </section>
    );
  }

  const { novel, stats } = data;

  return (
    <section className="py-8" data-testid="novel-info-section">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-3 gap-8">
          {/* About / Description */}
          <div className="md:col-span-2">
            <h2 className="text-xl font-bold text-white mb-4">About</h2>
            <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">
              {novel?.description || novel?.synopsis || 'No description available.'}
            </p>
            
            {/* Tags */}
            {props.showTags !== false && novel?.tags && novel.tags.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-medium text-zinc-400 mb-2">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {novel.tags.map((tag, i) => (
                    <Badge key={i} variant="outline" className="border-zinc-700 text-zinc-300">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Stats Sidebar */}
          {props.showStats !== false && (
            <div className="space-y-4">
              <Card className="bg-zinc-800/50 border-zinc-700">
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-zinc-400" />
                    <div>
                      <p className="text-xs text-zinc-500">Last Updated</p>
                      <p className="text-sm text-white">
                        {novel?.updated_at 
                          ? new Date(novel.updated_at).toLocaleDateString()
                          : 'Unknown'
                        }
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-zinc-400" />
                    <div>
                      <p className="text-xs text-zinc-500">Language</p>
                      <p className="text-sm text-white">{novel?.language || 'English'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <BookText className="w-5 h-5 text-zinc-400" />
                    <div>
                      <p className="text-xs text-zinc-500">Status</p>
                      <p className="text-sm text-white capitalize">{novel?.status || 'Ongoing'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-zinc-400" />
                    <div>
                      <p className="text-xs text-zinc-500">Maturity</p>
                      <p className="text-sm text-white">
                        {novel?.is_mature ? 'Mature (18+)' : 'All Ages'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// NOVEL COMMENTS SECTION
// ============================================================================

export const NovelCommentsSection = ({
  block,
  novelId: propNovelId,
  isPreview = false,
}) => {
  const { novelId: paramNovelId } = useParams();
  const novelId = propNovelId || paramNovelId;
  const { user } = useAuth();
  const { comments, setComments, loading, error } = useNovelComments(novelId, 20);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const props = block?.props || {};

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to comment');
      return;
    }
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      const response = await apiClient.post(`/novels/${novelId}/comments`, {
        content: newComment.trim(),
      });
      setComments(prev => [response.data, ...prev]);
      setNewComment('');
      toast.success('Comment posted!');
    } catch (err) {
      console.error('Error posting comment:', err);
      toast.error('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-8 bg-zinc-900/30" data-testid="novel-comments-section">
      <div className="container mx-auto px-6">
        <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-orange-500" />
          {props.heading || 'Comments'}
          <Badge variant="secondary" className="ml-2 bg-zinc-800 text-zinc-300">
            {comments.length}
          </Badge>
        </h2>

        {/* Comment Form */}
        {props.allowComments !== false && (
          <form onSubmit={handleSubmitComment} className="mb-8">
            <Textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={user ? "Share your thoughts..." : "Login to comment"}
              disabled={!user || submitting}
              className="bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500 mb-3"
              rows={3}
            />
            <Button 
              type="submit" 
              disabled={!user || !newComment.trim() || submitting}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Posting...</>
              ) : (
                'Post Comment'
              )}
            </Button>
          </form>
        )}

        {/* Comments List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex gap-3 animate-pulse">
                <div className="w-10 h-10 bg-zinc-800 rounded-full" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-zinc-800 rounded w-1/4" />
                  <div className="h-4 bg-zinc-800 rounded w-3/4" />
                </div>
              </div>
            ))}
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-12 bg-zinc-800/30 rounded-lg">
            <MessageCircle className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No comments yet</p>
            <p className="text-zinc-500 text-sm mt-1">Be the first to share your thoughts!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {comments.map((comment, index) => (
              <div key={comment.id || index} className="flex gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={comment.user?.avatar_url} />
                  <AvatarFallback className="bg-orange-500 text-white text-sm">
                    {(comment.user?.display_name || 'U')[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white font-medium text-sm">
                      {comment.user?.display_name || 'Reader'}
                    </span>
                    <span className="text-zinc-500 text-xs">
                      {comment.created_at && new Date(comment.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-zinc-300 text-sm">{comment.content}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// ============================================================================
// RELATED NOVELS SECTION
// ============================================================================

export const RelatedNovelsSection = ({
  block,
  novelId: propNovelId,
  isPreview = false,
}) => {
  const { novelId: paramNovelId } = useParams();
  const novelId = propNovelId || paramNovelId;
  const { related, loading, error } = useRelatedNovels(novelId);

  const props = block?.props || {};

  if (loading) {
    return (
      <section className="py-8">
        <div className="container mx-auto px-6">
          <div className="h-8 bg-zinc-800 rounded w-1/4 mb-6 animate-pulse" />
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="aspect-[2/3] bg-zinc-800 rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (related.length === 0) return null;

  return (
    <section className="py-8" data-testid="related-novels-section">
      <div className="container mx-auto px-6">
        <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <Zap className="w-5 h-5 text-orange-500" />
          {props.heading || 'More Like This'}
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {related.slice(0, props.maxItems || 6).map((novel) => (
            <Link
              key={novel.id}
              to={`/novel/${novel.id}`}
              className="group"
            >
              <div className="aspect-[2/3] bg-zinc-800 rounded-lg overflow-hidden mb-2">
                {novel.cover_url ? (
                  <img
                    src={novel.cover_url}
                    alt={novel.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <BookText className="w-8 h-8 text-zinc-600" />
                  </div>
                )}
              </div>
              <h3 className="text-white text-sm font-medium truncate group-hover:text-orange-400 transition-colors">
                {novel.title}
              </h3>
              <p className="text-zinc-500 text-xs truncate">
                {novel.author_name || 'Author'}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// NOVEL CREATOR SPOTLIGHT SECTION
// ============================================================================

export const NovelCreatorSpotlightSection = ({
  block,
  novelId: propNovelId,
  isPreview = false,
}) => {
  const { novelId: paramNovelId } = useParams();
  const novelId = propNovelId || paramNovelId;
  const { data, loading, error } = useNovelData(novelId);
  const { user } = useAuth();
  const navigate = useNavigate();

  const props = block?.props || {};

  if (loading) {
    return (
      <section className="py-8 bg-zinc-900/50">
        <div className="container mx-auto px-6">
          <SectionLoading />
        </div>
      </section>
    );
  }

  if (error || !data?.creator) {
    return null; // Silently hide if no creator data
  }

  const { creator, novel } = data;

  const handleFollow = () => {
    if (!user) {
      toast.error('Please login to follow');
      navigate('/auth');
      return;
    }
    toast.success('Follow feature coming soon!');
  };

  return (
    <section className="py-8 bg-zinc-900/50" data-testid="novel-creator-section">
      <div className="container mx-auto px-6">
        <h2 className="text-xl font-bold text-white mb-6">
          {props.heading || 'About the Author'}
        </h2>

        <Card className="bg-zinc-800/50 border-zinc-700">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-shrink-0">
                <Avatar className="h-24 w-24 border-2 border-orange-500">
                  <AvatarImage src={creator.avatar_url} />
                  <AvatarFallback className="bg-orange-500 text-white text-2xl">
                    {(creator.display_name || 'A')[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </div>
              
              <div className="flex-1">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <Link 
                      to={`/u/${creator.handle}`}
                      className="text-xl font-bold text-white hover:text-orange-400 transition-colors"
                    >
                      {creator.display_name}
                    </Link>
                    <p className="text-zinc-400">@{creator.handle}</p>
                  </div>
                  <Button 
                    onClick={handleFollow}
                    variant="outline" 
                    className="border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Follow
                  </Button>
                </div>
                
                <p className="text-zinc-300 text-sm mb-4 line-clamp-3">
                  {creator.bio || 'This author hasn\'t written a bio yet.'}
                </p>
                
                <div className="flex gap-6 text-sm">
                  <div className="text-center">
                    <p className="text-xl font-bold text-white">
                      {(creator.metrics_cache?.followers || 0).toLocaleString()}
                    </p>
                    <p className="text-zinc-500">Followers</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-white">
                      {(creator.metrics_cache?.works || 0)}
                    </p>
                    <p className="text-zinc-500">Works</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

// ============================================================================
// NOVEL CTA SECTION
// ============================================================================

export const NovelCTASection = ({
  block,
  novelId: propNovelId,
  isPreview = false,
}) => {
  const { novelId: paramNovelId } = useParams();
  const novelId = propNovelId || paramNovelId;
  const { data } = useNovelData(novelId);
  const { user } = useAuth();
  const navigate = useNavigate();

  const props = block?.props || {};

  const handleAction = (action) => {
    if (!user) {
      toast.error('Please login first');
      navigate('/auth');
      return;
    }
    toast.success(`${action} feature coming soon!`);
  };

  return (
    <section 
      className="py-12"
      style={{
        background: 'linear-gradient(to right, #f97316, #ea580c)',
      }}
      data-testid="novel-cta-section"
    >
      <div className="container mx-auto px-6 text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
          {props.heading || 'Support this Novel'}
        </h2>
        <p className="text-white/80 mb-6 max-w-2xl mx-auto">
          {props.subheading || 'Help the author continue writing by subscribing, tipping, or sharing with friends.'}
        </p>
        
        <div className="flex flex-wrap justify-center gap-4">
          <Button 
            size="lg" 
            className="bg-white text-orange-600 hover:bg-zinc-100 font-bold"
            onClick={() => handleAction('Subscribe')}
          >
            <Heart className="w-5 h-5 mr-2" />
            Subscribe
          </Button>
          <Button 
            size="lg" 
            variant="outline" 
            className="border-2 border-white text-white hover:bg-white/10"
            onClick={() => handleAction('Tip')}
          >
            <DollarSign className="w-5 h-5 mr-2" />
            Send Tip
          </Button>
          <Button 
            size="lg" 
            variant="outline" 
            className="border-2 border-white text-white hover:bg-white/10"
            onClick={() => {
              const url = window.location.href;
              navigator.clipboard.writeText(url);
              toast.success('Link copied!');
            }}
          >
            <Share2 className="w-5 h-5 mr-2" />
            Share
          </Button>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// EXPORTS
// ============================================================================

// Component map for Page Builder rendering
export const NOVEL_SECTION_COMPONENTS = {
  // With section_ prefix
  section_novel_hero: NovelHeroSection,
  section_chapter_list: ChapterListSection,
  section_novel_info: NovelInfoPanelSection,
  section_novel_comments: NovelCommentsSection,
  section_related_novels: RelatedNovelsSection,
  section_novel_creator: NovelCreatorSpotlightSection,
  section_novel_cta: NovelCTASection,
  // Without prefix (template IDs)
  novel_hero: NovelHeroSection,
  chapter_list: ChapterListSection,
  novel_info_panel: NovelInfoPanelSection,
  novel_comments: NovelCommentsSection,
  related_novels: RelatedNovelsSection,
  novel_creator_spotlight: NovelCreatorSpotlightSection,
  novel_cta: NovelCTASection,
};

export default {
  NovelHeroSection,
  ChapterListSection,
  NovelInfoPanelSection,
  NovelCommentsSection,
  RelatedNovelsSection,
  NovelCreatorSpotlightSection,
  NovelCTASection,
  NOVEL_SECTION_COMPONENTS,
  // Hooks
  useNovelData,
  useChapters,
  useNovelComments,
  useRelatedNovels,
};
